import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
    VStack,
    HStack,
    Box,
    FormControl,
    Input,
    Checkbox,
    WarningIcon
} from 'native-base';
import Button from '~/shared/Button';
import React, { useEffect, useState } from 'react';
import { useFormStyles, useGeneralStyles } from '~/shared/styles';
import { Caption } from '~/shared/Typography';
import Pressable from '~/shared/Pressable';
import {
    useSendEmailVerificationCode,
    useVerifyEmailCode,
    useSpringBigStore,
    i18nFn,
} from '@springbig/shared';

type EmailVerificationFormProps = {
    t: i18nFn
    isRequiredTerms?: boolean;
    walletId: string;
    onCancel: () => void;
    onProceed: (validatedEmail: string) => void;
    emailVerificationFormType?: EmailVerificationFormType;
};

type ConsumerApiError = {
    data?: {
        errors?: string;
    };
};

type EmailVerificationFormType = 'springbigpay' | 'ordersPage' | 'giftCardInbox';

const EmailVerificationForm = ({
    t,
    isRequiredTerms,
    walletId,
    onCancel,
    onProceed,
    emailVerificationFormType = 'springbigpay'
}: EmailVerificationFormProps) => {

    const gStyle = useGeneralStyles();

    const {
        input_background,
        input_border,
        input_label,
        input_text
    } = useFormStyles();

    const { member } = useSpringBigStore();
    const [apiError, setApiError] = useState<string | null>(null);
    const [isPinCodeStep, setIsPinCodeStep] = useState<boolean>(false);
    const [isTermsAcknowledged, setIsTermsAcknowledged] = useState<boolean>(false);
    const [termsUnchecked, setTermsUnchecked] = useState<boolean>(false);
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);
    const [isResentCode, setIsResentCode] = useState<boolean>(false);

    const schemaEmail = yup.object().shape({
        email: yup
            .string()
            .required(t('validation.email_required'))
            .matches(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i, t('validation.email_invalid'))
            .trim()
    });

    const schemaCode = yup.object().shape({
        code: yup
            .string()
            .required(t('validation.code_required'))
            .matches(/.{6}/i, t('validation.code_invalid'))
            .trim()
    });

    type FormDataEmail = yup.InferType<typeof schemaEmail>;
    type FormDataCode = yup.InferType<typeof schemaCode>;

    const { control: controlEmail, handleSubmit: handleSubmitEmail, formState: { errors: errorsEmail } } = useForm({
        resolver: yupResolver(schemaEmail)
    });

    const { control: controlCode, handleSubmit: handleSubmitCode, formState: { errors: errorsCode } } = useForm({
        resolver: yupResolver(schemaCode)
    });

    const { field: emailField } = useController({
        control: controlEmail,
        defaultValue: currentEmail || '',
        name: 'email',
    });

    const { field: codeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'code',
    });

    const {
        mutate: sendVerificationCode,
        isLoading: isLoadingEmail,
        error: errorEmail
    } = useSendEmailVerificationCode();

    const {
        mutate: verifyEmailCode,
        isLoading: isLoadingCode,
        error: errorCode
    } = useVerifyEmailCode();

    useEffect(() => {

        if (errorEmail) {
            setApiError((errorEmail as ConsumerApiError)?.data?.errors);
        }

        if (errorCode) {
            setApiError((errorCode as ConsumerApiError)?.data?.errors);
        }
    }, [errorCode, errorEmail]);

    const onSubmitEmail = (data: FormDataEmail): void => {

        if (isRequiredTerms && !isTermsAcknowledged) {
            setTermsUnchecked(true);
            return;
        }

        setApiError(null);
        setTermsUnchecked(false);
        setCurrentEmail(data.email);

        sendVerificationCode({
            email: data.email,
            member_id: walletId,
        }, {
            onSuccess: () => {

                setIsPinCodeStep(true);
            }
        });
    };

    const resendCode = (): void => {

        if (isResentCode) {
            return;
        }

        setApiError(null);
        sendVerificationCode({
            email: currentEmail,
            member_id: walletId,
        }, {
            onSuccess: () => {

                setIsResentCode(true);
                // 1 minute cool down
                setTimeout(() => setIsResentCode(false), 1000 * 60);
            }
        });
    };

    const onSubmitCode = (data: FormDataCode): void => {

        setApiError(null);
        verifyEmailCode({
            email: currentEmail,
            code: data.code,
            member_id: walletId,
        }, {
            onSuccess: () => {

                onProceed(currentEmail);
            }
        });
    };

    const handleLinkPress = (url: string): void => {

        if (!url) {
            return;
        }

        window.open(url, '_blank');
    };

    return (
        <VStack space={8} px={4}>
            { !isPinCodeStep ?
                <VStack space={8}>
                    <FormControl isInvalid={!!errorsEmail.email}>
                        <FormControl.Label color={input_label}>{
                            t('subscriptions.email_field')
                        }</FormControl.Label>
                        <Input
                            {...emailField}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            isInvalid={!!errorsEmail.email}
                            isDisabled={isLoadingEmail}
                            maxLength={255}
                            returnKeyType='done'
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { errorsEmail.email?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    { isRequiredTerms &&
                        <FormControl isInvalid={termsUnchecked}>
                            <Box flexDirection={'row'}>
                                <Checkbox
                                    flex={1}
                                    marginRight={2}
                                    alignSelf={'flex-start'}
                                    isChecked={isTermsAcknowledged}
                                    value={isTermsAcknowledged.toString()}
                                    isDisabled={isLoadingEmail}
                                    onChange={(value) => {

                                        if (value) {
                                            setIsTermsAcknowledged(value);
                                        }
                                    }}
                                    _checked={{
                                        value: '',
                                        bg: gStyle.button_background
                                    }}
                                />
                                <Pressable
                                    onPress={() => handleLinkPress(member.subs_terms)}
                                    flexDirection={'row'}
                                    flexWrap={'wrap'}
                                    flex={1}
                                >
                                    <Caption>{ t('subscriptions.terms_prefix') }</Caption>
                                    <Caption color={gStyle.link}>
                                        { t('subscriptions.terms_suffix') }
                                    </Caption>
                                </Pressable>
                            </Box>
                            <FormControl.ErrorMessage
                                leftIcon={<WarningIcon size={3} />}
                            >
                                { t('subscriptions.terms_error') }
                            </FormControl.ErrorMessage>
                        </FormControl>
                    }

                    <Button
                        bg={gStyle.button_background}
                        _text={{ color: gStyle.button_text }}
                        onPress={handleSubmitEmail(onSubmitEmail)}
                        isLoading={isLoadingEmail}
                    >
                        {
                            emailVerificationFormType === 'giftCardInbox' ?
                                t('giftCard.inboxSendCodeButton') :
                                t('subscriptions.next') }
                    </Button>

                </VStack>
                :
                <VStack space={4}>
                    <FormControl isInvalid={!!errorsCode.code}>
                        <Caption textAlign={'center'}>{
                            t('subscriptions.code_sent')
                        }</Caption>
                        <FormControl.Label color={input_label}>{
                            t('subscriptions.code_field')
                        }</FormControl.Label>
                        <Input
                            {...codeField}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            isInvalid={!!errorsCode.code}
                            isDisabled={isLoadingCode}
                            maxLength={6}
                            returnKeyType='done'
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { errorsCode.code?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <HStack justifyContent={'space-evenly'} space={4} flexWrap={'wrap'} alignItems={'center'}>
                        <Button
                            _text={{ color: gStyle.link }}
                            onPress={resendCode}
                            isLoading={isLoadingEmail}
                            isDisabled={isLoadingCode}
                        >
                            { isResentCode ? t('subscriptions.code_sent') : t('subscriptions.code_resend') }
                        </Button>

                        <Button
                            _text={{ color: gStyle.link }}
                            isDisabled={isLoadingCode}
                            onPress={() => {

                                setApiError(null);
                                setIsPinCodeStep(false);
                            }}
                        >
                            { t('subscriptions.change_email') }
                        </Button>
                    </HStack>

                    <Button
                        bg={gStyle.button_background}
                        _text={{ color: gStyle.button_text }}
                        onPress={handleSubmitCode(onSubmitCode)}
                        isLoading={isLoadingCode}
                    >
                        { t('subscriptions.verify_email') }
                    </Button>
                </VStack>
            }

            {emailVerificationFormType === 'springbigpay' ? <VStack space={8}>
                <Button
                    bg={gStyle.button_neutral_background}
                    _text={{ color: gStyle.button_neutral_text }}
                    onPress={onCancel}
                    isDisabled={isLoadingCode || isLoadingEmail}
                >
                    { t('subscriptions.back_button') }
                </Button>

                <FormControl isInvalid={!!apiError}>
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={5} />}
                    >
                        { t('subscriptions.email_error', [apiError]) }
                    </FormControl.ErrorMessage>
                </FormControl>
            </VStack> : <></>}
        </VStack>
    );
};

export default EmailVerificationForm;
